import { type AskPunttProjectData } from "@mg/schemas/src/christo/askPuntt";
import {
  CheckCircle,
  CircleNotch,
  MagnifyingGlass,
} from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";

import { Markdown } from "../../../components/Markdown";

/** Drop a leading "Key findings" heading — the card already renders that label. */
export function stripRedundantFindingsHeading(text: string): string {
  return text
    .replace(/^\s*(?:#+\s*)?(?:\*\*)?key\s*findings\s*:?\s*(?:\*\*)?\s*/i, "")
    .replace(/^\s*\n+/, "")
    .trimStart();
}

/**
 * Shared key-findings block from `ProjectStatusPane`, reused in the chat-details
 * rail so multi-ticket browse matches the old sidebar findings presentation.
 */
export function KeyFindingsSummarySection({
  findingsSummary,
  ticketFindings,
  isKillerFlowStreaming = false,
  hasActiveStage = false,
  findingsLiveInBar = false,
}: {
  findingsSummary?: string;
  ticketFindings?: AskPunttProjectData["ticketFindings"];
  isKillerFlowStreaming?: boolean;
  hasActiveStage?: boolean;
  /** Deep progress bar is already listing findings — do not say they appear later. */
  findingsLiveInBar?: boolean;
}) {
  const { t } = useTranslation("ui");
  const findingsText = stripRedundantFindingsHeading(
    findingsSummary?.trim() ?? "",
  );

  if (isKillerFlowStreaming) {
    return (
      <div
        className="rounded-lg border border-puntt-neutral-gray-6 bg-puntt-neutral-gray-2/30 p-3"
        data-testid="chat-details-findings-reviewing"
      >
        <div className="flex items-start gap-2">
          <CircleNotch
            size={14}
            aria-hidden="true"
            className="mt-0.5 shrink-0 animate-spin text-puntt-accent-9"
          />
          <p className="text-[13px] leading-relaxed text-puntt-neutral-gray-10">
            {t("keyFindingsSummarySection.reviewingInParallel")}
          </p>
        </div>
      </div>
    );
  }

  if (findingsText) {
    const allClean = ticketFindings?.every((f) => f.severity === "clean");
    if (allClean) {
      return (
        <div
          className="border-green-200 bg-green-50 rounded-lg border p-3"
          data-testid="chat-details-findings-clean"
        >
          <div className="flex items-start gap-2">
            <CheckCircle
              size={14}
              weight="fill"
              aria-hidden="true"
              className="text-green-600 mt-0.5 shrink-0"
            />
            <p className="text-green-800 text-[13px] font-medium leading-relaxed">
              {findingsText}
            </p>
          </div>
        </div>
      );
    }

    return (
      <div
        className="rounded-lg border border-puntt-neutral-gray-6 bg-puntt-neutral-gray-2/30 p-4"
        data-testid="chat-details-findings-body"
      >
        <div className="mb-2 flex items-center gap-2">
          <MagnifyingGlass
            size={14}
            weight="fill"
            aria-hidden="true"
            className="text-puntt-accent-9"
          />
          <span className="text-[13px] font-bold text-puntt-neutral-gray-12">
            {t("keyFindingsLower")}
          </span>
        </div>
        <Markdown className="text-sm leading-relaxed text-puntt-neutral-gray-11">
          {findingsText}
        </Markdown>
      </div>
    );
  }

  if (hasActiveStage) {
    return (
      <div
        className="rounded-lg border border-puntt-neutral-gray-6 bg-puntt-neutral-gray-2/30 p-3"
        data-testid="chat-details-findings-in-progress"
      >
        <div className="flex items-start gap-2">
          <CircleNotch
            size={14}
            aria-hidden="true"
            className="mt-0.5 shrink-0 animate-spin text-puntt-accent-9"
          />
          <p className="text-[13px] leading-relaxed text-puntt-neutral-gray-10">
            {t(
              findingsLiveInBar
                ? "findingsAppearInProgress"
                : "findingsWillAppear",
            )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <p
      className="text-[13px] leading-relaxed text-puntt-neutral-gray-11"
      data-testid="chat-details-findings-empty"
    >
      {t("noKeyFindingsYet")}
    </p>
  );
}
